import type { AccountEntity } from '@actual-app/core/types/models';
import type { SyncedPrefs } from '@actual-app/core/types/prefs';

export const portfolioGroups = [
  { id: 'cash', label: '현금' },
  { id: 'savings', label: '저축' },
  { id: 'investment', label: '투자' },
  { id: 'property', label: '부동산·보증금' },
  { id: 'debt', label: '대출·부채' },
  { id: 'other', label: '미분류' },
] as const;

export type PortfolioGroup = (typeof portfolioGroups)[number]['id'];
export type AccountBalances = Readonly<Record<string, number | null>>;

export function getPortfolioGroup(
  account: Pick<AccountEntity, 'id' | 'name'>,
  prefs: SyncedPrefs,
): PortfolioGroup {
  const saved = prefs[`account-portfolio-group-${account.id}`];
  const group = portfolioGroups.find(group => group.id === saved);
  if (group) {
    return group.id;
  }

  // Name-based defaults are only display suggestions. Users can override
  // them without changing an account's budget status or its transactions.
  const name = account.name.toLowerCase();
  if (/대출|부채|카드|loan|liability|credit card/.test(name)) {
    return 'debt';
  }
  if (/부동산|보증금|전세|property|real estate|housing deposit/.test(name)) {
    return 'property';
  }
  if (/주식|증권|코인|투자|펀드|stock|brokerage|crypto|investment/.test(name)) {
    return 'investment';
  }
  if (/청약|저축|적금|예금|연금|saving|pension/.test(name)) {
    return 'savings';
  }
  if (/현금|비상금|입출금|통장|cash|checking|current account/.test(name)) {
    return 'cash';
  }
  return 'other';
}

export function groupPortfolioAccounts(
  accounts: readonly AccountEntity[],
  prefs: SyncedPrefs,
) {
  const active = accounts.filter(
    account => !account.closed && !account.tombstone,
  );
  return portfolioGroups
    .map(group => ({
      ...group,
      accounts: active.filter(
        account => getPortfolioGroup(account, prefs) === group.id,
      ),
    }))
    .filter(group => group.accounts.length > 0);
}

export function sumAccountBalances(
  accounts: readonly Pick<AccountEntity, 'id'>[],
  balances: AccountBalances,
): number | null {
  let sum = 0;
  for (const account of accounts) {
    const balance = balances[account.id];
    if (typeof balance !== 'number' || !Number.isFinite(balance)) {
      return null;
    }
    sum += balance;
  }
  return sum;
}

export function calculatePortfolio(
  accounts: readonly AccountEntity[],
  balances: AccountBalances,
) {
  let assets = 0;
  let liabilities = 0;
  for (const account of accounts) {
    if (account.closed || account.tombstone) {
      continue;
    }
    const balance = balances[account.id];
    if (typeof balance !== 'number' || !Number.isFinite(balance)) {
      return null;
    }
    if (balance < 0) {
      liabilities -= balance;
    } else {
      assets += balance;
    }
  }
  return { assets, liabilities, netWorth: assets - liabilities };
}

export function getPortfolioReorderTarget(
  accounts: readonly AccountEntity[],
  prefs: SyncedPrefs,
  id: string,
  dropPos: 'top' | 'bottom' | null,
  targetId: string,
): string | null | undefined {
  const source = accounts.find(account => account.id === id);
  const target = accounts.find(account => account.id === targetId);
  if (
    !source ||
    !target ||
    source.closed !== target.closed ||
    (!source.closed &&
      (source.offbudget !== target.offbudget ||
        getPortfolioGroup(source, prefs) !== getPortfolioGroup(target, prefs)))
  ) {
    return undefined;
  }
  if (dropPos !== 'bottom') {
    return targetId;
  }
  const siblings = accounts.filter(
    account =>
      !account.tombstone &&
      (source.closed
        ? !!account.closed
        : !account.closed &&
          account.offbudget === source.offbudget &&
          getPortfolioGroup(account, prefs) ===
            getPortfolioGroup(source, prefs)),
  );
  const next = siblings.findIndex(account => account.id === targetId) + 1;
  return siblings[next]?.id ?? null;
}
